import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { Button, Col, Row } from "react-bootstrap";
import { connect } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import {
  DocuclipperToken,
  Integration,
} from "../../../docuclipper/DocuclipperTypes";
import excelIcon from "../../../icons/excel.png";
import myobIcon from "../../../icons/myob.jpeg";
import quickbooksIcon from "../../../icons/qb-logo-horizontal-preferred.svg";

import sageIcon from "../../../icons/sage.png";
import xeroIcon from "../../../icons/xero.png";
import { postNavigate, sendGAEvent } from "../../../utils/utils";
import CollapsibleBtnText from "../../Docuclipper/CollapsibleBtnText";
import TrackedButton from "../../Docuclipper/TrackedButton";
import IntegrationActions from "./IntegrationsActions";

/* tslint:disable:no-var-requires */
const qs = require("qs");

const API_BASE_DEV =
  process.env.NODE_ENV === "production" ? "" : "http://localhost:8080";
const INTEGRATION_QUICKBOOKS_URL = `${API_BASE_DEV}/api/v1/protected/auth/intuit`;
const INTEGRATION_XERO_URL = `${API_BASE_DEV}/api/v1/protected/auth/xero`;
const INTEGRATION_GDOCS_BASE = `${API_BASE_DEV}/api/v1/protected/auth/gdocs`;
const INTEGRATION_ONEDRIVE_URL = `${API_BASE_DEV}/api/v1/protected/auth/microsoft-onedrive`;
const INTEGRATION_DROPBOX_URL = `${API_BASE_DEV}/api/v1/protected/auth/dropbox`;
const INTEGRATION_BOX_URL = `${API_BASE_DEV}/api/v1/protected/auth/box`;

const ONEDRIVE_SCOPES = [
  "https://graph.microsoft.com/Files.ReadWrite",
  "https://graph.microsoft.com/Files.Read",
];

export const INTEGRATIONS = {
  googleProfile: {
    scope: "https://www.googleapis.com/auth/userinfo.profile",
    url: `${INTEGRATION_GDOCS_BASE}?integration=googleSpreadsheets`,
  },
  googleDocuments: {
    scope: "https://www.googleapis.com/auth/documents",
    url: `${INTEGRATION_GDOCS_BASE}?integration=googleDocuments`,
  },
  googleDrive: {
    scope: "https://www.googleapis.com/auth/drive",
    url: `${INTEGRATION_GDOCS_BASE}?integration=googleDrive`,
  },
  googleSpreadsheets: {
    scope: "https://www.googleapis.com/auth/spreadsheets",
    url: `${INTEGRATION_GDOCS_BASE}?integration=googleSpreadsheets`,
  },
  quickbooks: {
    scope: "intuit.quickbooks.accounting",
    url: `${INTEGRATION_QUICKBOOKS_URL}?integration=quickbooks`,
  },
  xero: {
    scope: "xero.accounting",
    url: `${INTEGRATION_XERO_URL}?integration=xero`,
  },
  gmail: {
    scope: "https://www.googleapis.com/auth/gmail.readonly",
    url: `${INTEGRATION_GDOCS_BASE}?integration=gmail`,
  },
  oneDrive: {
    scope: ONEDRIVE_SCOPES[0],
    url: `${INTEGRATION_ONEDRIVE_URL}?source=docuclipper`,
  },
  dropbox: {
    scope: "dropbox.files",
    url: INTEGRATION_DROPBOX_URL,
  },
  box: {
    scope: "box.files",
    url: INTEGRATION_BOX_URL,
  },
};

export const goToGdocs = (maybeNext: string) =>
  postNavigate(`${INTEGRATIONS.googleSpreadsheets.url}${maybeNext}`);
export const goToGdrive = (maybeNext: string) =>
  postNavigate(`${INTEGRATIONS.googleDrive.url}${maybeNext}`);
export const goToGmail = (maybeNext: string) =>
  postNavigate(`${INTEGRATIONS.gmail.url}${maybeNext}`);
export const goToOneDrive = (maybeNext: string) =>
  postNavigate(`${INTEGRATIONS.oneDrive.url}${maybeNext}`);
// Dropbox/Box base URLs have no existing query string, so a leading `&next=`
// from the caller needs to become `?next=` once `?token=` is no longer there.
const firstParam = (s: string) => s.replace(/^&/, "?");
export const goToDropbox = (maybeNext: string) =>
  postNavigate(`${INTEGRATIONS.dropbox.url}${firstParam(maybeNext)}`);
export const goToBox = (maybeNext: string) =>
  postNavigate(`${INTEGRATIONS.box.url}${firstParam(maybeNext)}`);
export const goToXeroFn = (next: string, tagLevelId?: number) => {
  const tagLevelParam = tagLevelId ? `&tagLevelId=${tagLevelId}` : "";
  postNavigate(`${INTEGRATIONS.xero.url}${next}${tagLevelParam}`);
};
export const goToXero = () => {
  const next = `&next=${encodeURIComponent(`/integrations`)}`;
  goToXeroFn(next);
};

type Props = {
  integrations: Integration[];
  testFn: (...args: any[]) => any;
  deleteFn: (...args: any[]) => any;
};

type ReduxProps = {
  token: DocuclipperToken;
};

export const goToQuickbooksFn = (next: string, tagLevelId?: number) => {
  const tagLevelParam = tagLevelId ? `&tagLevelId=${tagLevelId}` : "";
  postNavigate(`${INTEGRATIONS.quickbooks.url}${next}${tagLevelParam}`);
};

export const goToQuickbooks = () => {
  const next = `&next=${encodeURIComponent(`/integrations`)}`;
  goToQuickbooksFn(next);
};

export const IntegrationsTable: React.SFC<
  Props & ReduxProps & RouteComponentProps
> = ({ integrations, location, history, testFn, deleteFn, token }) => {
  // const [integrations,setIntegrations] = React.useState<Integration[]>([]);
  let query;
  if (location) {
    query = qs.parse(location.search, {
      ignoreQueryPrefix: true,
    });
  }

  const googleSpreadsheetsIntegration = integrations.filter(
    (i) => i.scope === INTEGRATIONS.googleSpreadsheets.scope,
  );
  const googleDriveIntegration = integrations.filter(
    (i) => i.scope === INTEGRATIONS.googleDrive.scope,
  );
  const quickbooksIntegrations = integrations.filter(
    (i) => i.scope === INTEGRATIONS.quickbooks.scope,
  );
  const xeroIntegrations = integrations.filter(
    (i) => i.scope === INTEGRATIONS.xero.scope,
  );
  const gmailIntegrations = integrations.filter(
    (i) => i.scope === INTEGRATIONS.gmail.scope,
  );
  const oneDriveIntegrations = integrations.filter(
    (i) => ONEDRIVE_SCOPES.includes(i.scope),
  );
  const dropboxIntegrations = integrations.filter(
    (i) => i.scope === INTEGRATIONS.dropbox.scope,
  );
  const boxIntegrations = integrations.filter(
    (i) => i.scope === INTEGRATIONS.box.scope,
  );

  const maybeNext = query.next ? `&next=${query.next}` : "";

  const sheetsActions = (
    <>
      <IntegrationActions
        integrations={googleSpreadsheetsIntegration}
        integrationsInfo={INTEGRATIONS}
        type="googleSpreadsheets"
        testFn={testFn}
        deleteFn={deleteFn}
      />
    </>
  );
  const driveActions = (
    <>
      <IntegrationActions
        integrations={googleDriveIntegration}
        integrationsInfo={INTEGRATIONS}
        type="googleDrive"
        testFn={testFn}
        deleteFn={deleteFn}
      />
    </>
  );
  const oneDriveActions = (
    <>
      <IntegrationActions
        integrations={oneDriveIntegrations}
        integrationsInfo={INTEGRATIONS}
        type="oneDrive"
        testFn={testFn}
        deleteFn={deleteFn}
      />
    </>
  );
  const dropboxActions = (
    <>
      <IntegrationActions
        integrations={dropboxIntegrations}
        integrationsInfo={INTEGRATIONS}
        type="dropbox"
        testFn={testFn}
        deleteFn={deleteFn}
      />
    </>
  );
  const boxActions = (
    <>
      <IntegrationActions
        integrations={boxIntegrations}
        integrationsInfo={INTEGRATIONS}
        type="box"
        testFn={testFn}
        deleteFn={deleteFn}
      />
    </>
  );
  const quickbooksActions = (
    <>
      <IntegrationActions
        integrations={quickbooksIntegrations}
        integrationsInfo={INTEGRATIONS}
        type="quickbooks"
        testFn={testFn}
        deleteFn={deleteFn}
      />
    </>
  );
  const xeroActions = (
    <>
      <IntegrationActions
        integrations={xeroIntegrations}
        integrationsInfo={INTEGRATIONS}
        type="xero"
        testFn={testFn}
        deleteFn={deleteFn}
      />
    </>
  );

  let gmailActions: any = null;
  if (
    token &&
    token.permissions &&
    token.permissions.includes("MANAGE_EMAIL_INTEGRATION")
  ) {
    gmailActions = (
      <div className="card">
        <div className="card-header">
          <strong>Gmail</strong>
          <TrackedButton
            variant="primary"
            action="connectToGmail"
            onClick={() => goToGmail(maybeNext)}
            className="float-right btn--add-gmail"
          >
            <CollapsibleBtnText icon="plus" text="Connect Gmail Account" />
          </TrackedButton>
        </div>
        <div className="card-body">
          <IntegrationActions
            integrations={gmailIntegrations}
            integrationsInfo={INTEGRATIONS}
            type="gmail"
            testFn={testFn}
            deleteFn={deleteFn}
          />
        </div>
      </div>
    );
  }
  return (
    <>
      <div className="h5 text-uppercase text-muted small mb-2 mt-1">
        Connections
      </div>
      <p className="text-muted small mb-3">
        Set up once. DocuClipper pulls documents from cloud storage or pushes
        extracted data into your accounting tool.
      </p>

      {/* quickbooks */}

      <div className="card">
        <div className="card-header">
          <img
            style={{ height: "48px" }}
            src={quickbooksIcon}
            alt="QuickBooks logo"
          />
          <strong className="ml-2">
            QuickBooks Online (Invoices & Receipts)
          </strong>
          <Button
            variant="outline-primary"
            className="float-right btn--add-quickbooks"
            onClick={() => {
              sendGAEvent({
                action: "qbo_connect_clicked",
                category: "integration",
              });
              history.push("/quickbooksaddnewcompany");
            }}
          >
            <FontAwesomeIcon icon="plus"></FontAwesomeIcon>
            <span className="ml-1">Add QuickBooks Company</span>
          </Button>
        </div>
        <div className="card-body">{quickbooksActions}</div>
      </div>
      {/* gdocs */}
      <div className="card">
        <div className="card-header">
          <img style={{ height: "24px" }} src={excelIcon} alt="Excel logo" />
          <strong className="ml-2">Google Sheets</strong>
          <TrackedButton
            action="addGoogleDocsSpreadsheetAccount"
            className="float-right btn--add-gdocs"
            onClick={() => goToGdocs(maybeNext)}
            variant="primary"
          >
            <CollapsibleBtnText icon="plus" text="Connect Google Account" />
          </TrackedButton>
        </div>
        <div className="card-body">{sheetsActions}</div>
      </div>

      {/* Google Drive */}
      <div className="card">
        <div className="card-header">
          <FontAwesomeIcon icon="folder" className="mx-2" />
          <strong className="ml-2">Google Drive</strong>
          <TrackedButton
            action="addGoogleDriveAccount"
            className="float-right btn--add-gdrive"
            onClick={() => goToGdrive(maybeNext)}
            variant="primary"
          >
            <CollapsibleBtnText icon="plus" text="Connect Google Drive" />
          </TrackedButton>
        </div>
        <div className="card-body">{driveActions}</div>
      </div>

      <div className="card">
        <div className="card-header">
          <FontAwesomeIcon icon={["fab", "microsoft"]} className="mx-2" />
          <strong className="ml-2">OneDrive (Auto-upload)</strong>
          <TrackedButton
            action="addOneDriveAccount"
            className="float-right btn--add-onedrive"
            onClick={() => goToOneDrive(maybeNext)}
            variant="primary"
          >
            <CollapsibleBtnText icon="plus" text="Connect OneDrive" />
          </TrackedButton>
        </div>
        <div className="card-body">{oneDriveActions}</div>
      </div>

      <div className="card">
        <div className="card-header">
          <FontAwesomeIcon
            icon={["fab", "dropbox"]}
            className="mx-2 text-info"
            title="Dropbox"
          />
          <strong className="ml-2">Dropbox (Auto-upload)</strong>
          <TrackedButton
            action="addDropboxAccount"
            className="float-right btn--add-dropbox"
            onClick={() => goToDropbox(maybeNext)}
            variant="primary"
          >
            <CollapsibleBtnText icon="plus" text="Connect Dropbox" />
          </TrackedButton>
        </div>
        <div className="card-body">{dropboxActions}</div>
      </div>

      <div className="card">
        <div className="card-header">
          <FontAwesomeIcon
            icon={["fas", "cube"]}
            className="mx-2 automations-box-icon"
            title="Box"
          />
          <strong className="ml-2">Box (Auto-upload)</strong>
          <TrackedButton
            action="addBoxAccount"
            className="float-right btn--add-box"
            onClick={() => goToBox(maybeNext)}
            variant="primary"
          >
            <CollapsibleBtnText icon="plus" text="Connect Box" />
          </TrackedButton>
        </div>
        <div className="card-body">{boxActions}</div>
      </div>

      {/* xero */}
      <div className="card">
        <div className="card-header">
          <img style={{ height: "24px" }} src={xeroIcon} alt="Xero logo" />
          <strong className="ml-2">Xero (Bank Transactions)</strong>
          <Button variant="outline-primary" className="float-right btn--add-xero" onClick={goToXero}>
            <FontAwesomeIcon icon="plus"></FontAwesomeIcon>
            <span className="ml-1">Connect Xero Account</span>
          </Button>
        </div>
        <div className="card-body">{xeroActions}</div>
      </div>

      {gmailActions}

      <div className="h5 text-uppercase text-muted small mb-2 mt-5">
        Supported export formats
      </div>
      <p className="text-muted small mb-3">
        No setup needed. Just download in your preferred format from the
        export menu. Help links below for importing into each tool.
      </p>

      {/* QuickBooks Online is also supported as a no-config download (Web
          Connect file). It's listed once at the top under Connections; the
          import-help link is included in its connection card subtitle to
          avoid showing the same logo twice on this page. */}
      {[
        {
          icon: excelIcon,
          name: "Excel (CSV & XLSX)",
          help: <>No configuration necessary.</>,
        },
        {
          icon: quickbooksIcon,
          name: "QuickBooks Desktop",
          help: (
            <Row>
              <Col>
                <div>No configuration necessary.</div>
              </Col>
              <Col>
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://www.docuclipper.com/docs/ui/user-guide/extracting-data/import-data-quickbooks-desktop/"
                >
                  <FontAwesomeIcon icon="question-circle" />
                  <span className="ml-1">
                    How to import Web Connect files into QuickBooks Desktop
                  </span>
                  <FontAwesomeIcon className="ml-2" icon="external-link-alt" />
                </a>
                <br />
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://www.docuclipper.com/docs/ui/user-guide/extracting-data/import-data-quickbooks-desktop-iif/"
                >
                  <FontAwesomeIcon icon="question-circle" />
                  <span className="ml-1">
                    How to import IIF files into QuickBooks Desktop
                  </span>
                  <FontAwesomeIcon className="ml-2" icon="external-link-alt" />
                </a>
              </Col>
            </Row>
          ),
        },
        {
          icon: xeroIcon,
          name: "Xero",
          help: (
            <Row>
              <Col>
                <div>No configuration necessary.</div>
              </Col>
              <Col>
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://www.docuclipper.com/docs/ui/user-guide/extracting-data/import-data-xero/"
                >
                  <FontAwesomeIcon icon="question-circle" />
                  <span className="ml-1">
                    How to import CSV files into Xero
                  </span>
                  <FontAwesomeIcon className="ml-2" icon="external-link-alt" />
                </a>
              </Col>
            </Row>
          ),
        },
        {
          icon: sageIcon,
          name: "Sage",
          help: (
            <Row>
              <Col>
                <div>No configuration necessary.</div>
              </Col>
              <Col>
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://www.docuclipper.com/docs/ui/user-guide/extracting-data/import-data-sage/"
                >
                  <FontAwesomeIcon icon="question-circle" />
                  <span className="ml-1">
                    How to import CSV files into Sage
                  </span>
                  <FontAwesomeIcon className="ml-2" icon="external-link-alt" />
                </a>
              </Col>
            </Row>
          ),
        },
        {
          icon: myobIcon,
          name: "MYOB",
          help: (
            <Row>
              <Col>
                <div>No configuration necessary.</div>
              </Col>
            </Row>
          ),
        },
      ].map(({ icon, name, help }, i) => (
        <div className="card" key={i}>
          <div className="card-header">
            <img
              style={{
                height: name.toLowerCase().includes("quickbooks")
                  ? "40px"
                  : "24px",
              }}
              src={icon}
              alt={`${name} logo`}
            />
            <strong className="ml-2">{name}</strong>
          </div>
          <div className="card-body">{help}</div>
        </div>
      ))}
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    token: state.Auth.token,
  };
};

export default connect(mapStateToProps, {})(withRouter(IntegrationsTable));
